import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import Stepper from '@material-ui/core/Stepper'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import { amplitudeEvent } from 'lib/amplitude'
import { useAuth } from 'lib/auth'
import { useDebounce } from 'lib/hooks'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import {
  useCoachCampaign,
  useCoachCampaignAudience,
  useCoachCampaignOptions,
  useCreateCoachCampaign,
  useSendCoachCampaign,
  useUpdateCoachCampaign,
} from '../api-hooks'
import {
  CAMPAIGNS_PATH,
  EMPTY_CAMPAIGN,
  MAX_SCHEDULE_DAYS,
  STEPS,
} from '../constants'
import {
  filtersToInput,
  isHandPicked,
  pluralize,
  toCampaignInput,
  toCampaignState,
} from '../format'
import AudienceStep from './audience-step'
import ContentStep from './content-step'
import ReviewStep from './review-step'
import useStyles from '../styles'

const LOCAL_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm"
// The audience query's page limit, so a draft's picks all show as chips.
const MAX_PICKED_PREVIEW = 100

const toOptions = data => {
  const coach = data?.currentUser?.attributes
  const lessonTypes = data?.lessonTypes || []
  const upcomingGroupEvents = data?.currentUser?.upcomingGroupEvents || []

  return {
    audience: {
      lessonTypes: lessonTypes.map(({ id, title }) => ({ id, label: title })),
      groupEvents: (data?.currentUser?.pastGroupEvents || []).map(
        ({ id, title, startDateTime }) => ({
          id,
          label: `${title} · ${DateTime.fromISO(startDateTime).toFormat(
            'LLL d, yyyy',
          )}`,
        }),
      ),
      programs: (coach?.jrlPrograms || []).map(({ id, name, year }) => ({
        id,
        label: year ? `${name} (${year})` : name,
      })),
      facilities: (coach?.facilities || []).map(({ id, name }) => ({
        id,
        label: name,
      })),
    },
    offerings: {
      lessonTypes: lessonTypes
        .filter(lessonType => lessonType.status === 'ACTIVE')
        .map(({ id, title }) => ({ id, title })),
      groupEvents: upcomingGroupEvents.map(({ id, title, startDateTime }) => ({
        id,
        title,
        startDateTime,
      })),
    },
  }
}

const defaultScheduledFor = () =>
  DateTime.local()
    .plus({ days: 1 })
    .set({ hour: 9, minute: 0 })
    .toFormat(LOCAL_INPUT_FORMAT)

export function CampaignWizardForm({ initialCampaign, hasPaidTier }) {
  const classes = useStyles()
  const history = useHistory()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const showError = useSnackbarError(enqueueSnackbar)
  const showSuccess = useSnackbarSuccess(enqueueSnackbar)

  const [campaign, setCampaign] = useState(initialCampaign)
  const [step, setStep] = useState(0)
  const [audienceMode, setAudienceMode] = useState(
    isHandPicked(initialCampaign.audienceFilters) ? 'pick' : 'filters',
  )
  const [pickedStudents, setPickedStudents] = useState([])
  const [nameError, setNameError] = useState(false)
  const [bodyError, setBodyError] = useState(false)
  const [delivery, setDelivery] = useState('now')
  const [scheduledFor, setScheduledFor] = useState(defaultScheduledFor)
  const [scheduleError, setScheduleError] = useState(null)
  const [busy, setBusy] = useState(false)

  const [createCampaign] = useCreateCoachCampaign()
  const [updateCampaign] = useUpdateCoachCampaign()
  const [sendCampaign] = useSendCoachCampaign()

  const { data: optionsData } = useCoachCampaignOptions({ skip: !hasPaidTier })
  const options = useMemo(() => toOptions(optionsData), [optionsData])

  const debouncedFilters = useDebounce(campaign.audienceFilters, 400)
  const waitingForPicks =
    audienceMode === 'pick' && debouncedFilters.studentIds.length === 0
  const {
    data: audienceData,
    loading: audienceLoading,
  } = useCoachCampaignAudience(filtersToInput(debouncedFilters), {
    skip: !hasPaidTier || waitingForPicks,
    limit: audienceMode === 'pick' ? MAX_PICKED_PREVIEW : undefined,
  })
  const audience = waitingForPicks
    ? { count: 0, maxRecipients: 0, students: [] }
    : audienceData?.coachCampaignAudience
  const audienceCount = audience?.count ?? 0
  const overLimit =
    Boolean(audience?.maxRecipients) && audienceCount > audience.maxRecipients
  const audienceReady =
    debouncedFilters === campaign.audienceFilters &&
    !audienceLoading &&
    audienceCount > 0 &&
    !overLimit

  const pickedIds = campaign.audienceFilters.studentIds
  useEffect(() => {
    const students = audienceData?.coachCampaignAudience?.students || []
    setPickedStudents(current => {
      const shown = new Set(current.map(student => student.id))
      const found = students.filter(
        student => pickedIds.includes(student.id) && !shown.has(student.id),
      )
      return found.length > 0 ? [...current, ...found] : current
    })
  }, [audienceData, pickedIds])

  // Picks not shown as chips yet (still loading, or past the preview limit)
  // are kept, so editing the chips never drops them.
  const pickStudents = students => {
    const shown = new Set(pickedStudents.map(student => student.id))
    const unshown = pickedIds.filter(id => !shown.has(id))
    setPickedStudents(students)
    change({
      audienceFilters: {
        ...campaign.audienceFilters,
        studentIds: [...unshown, ...students.map(student => student.id)],
      },
    })
  }

  const change = patch => {
    setCampaign(current => ({ ...current, ...patch }))
    if (patch.name?.trim()) setNameError(false)
    if (patch.body?.trim()) setBodyError(false)
  }

  const changeAudienceMode = mode => {
    setAudienceMode(mode)
    if (mode === 'filters') setPickedStudents([])
    change({
      audienceFilters: {
        ...campaign.audienceFilters,
        excludedStudentIds: [],
        ...(mode === 'filters' ? { studentIds: [] } : {}),
      },
    })
  }

  const persist = async () => {
    const input = toCampaignInput(campaign)
    try {
      const { data } = campaign.id
        ? await updateCampaign({ variables: { id: campaign.id, input } })
        : await createCampaign({ variables: { input } })
      const response = data?.updateCoachCampaign || data?.createCoachCampaign
      if (!response?.success) {
        showError(response?.message)
        return null
      }
      setCampaign(current => ({ ...current, id: response.campaign.id }))
      return response.campaign
    } catch (e) {
      showError()
      return null
    }
  }

  const validateStep = index => {
    if (index === 0 && !campaign.name.trim()) {
      setNameError(true)
      return false
    }
    if (index === 1 && !campaign.body.trim()) {
      setBodyError(true)
      return false
    }
    return true
  }

  const goNext = async () => {
    if (!validateStep(step)) return
    setBusy(true)
    const saved = await persist()
    setBusy(false)
    if (saved) {
      setStep(step + 1)
      window.scrollTo(0, 0)
    }
  }

  const saveDraft = async () => {
    if (!campaign.name.trim()) {
      setStep(0)
      setNameError(true)
      return
    }
    setBusy(true)
    const saved = await persist()
    setBusy(false)
    if (saved) {
      showSuccess('Draft saved')
      history.push(CAMPAIGNS_PATH)
    }
  }

  const minScheduledFor = DateTime.local()
    .plus({ minutes: 5 })
    .toFormat(LOCAL_INPUT_FORMAT)
  const maxScheduledFor = DateTime.local()
    .plus({ days: MAX_SCHEDULE_DAYS })
    .toFormat(LOCAL_INPUT_FORMAT)

  const send = async () => {
    let scheduledForIso = null
    if (delivery === 'later') {
      const scheduledAt = DateTime.fromFormat(scheduledFor, LOCAL_INPUT_FORMAT)
      if (!scheduledAt.isValid || scheduledAt <= DateTime.local()) {
        setScheduleError('Choose a time in the future')
        return
      }
      if (scheduledAt > DateTime.local().plus({ days: MAX_SCHEDULE_DAYS })) {
        setScheduleError(`Choose a time within ${MAX_SCHEDULE_DAYS} days`)
        return
      }
      setScheduleError(null)
      scheduledForIso = scheduledAt.toUTC().toISO()
    }

    setBusy(true)
    const saved = await persist()
    if (!saved) {
      setBusy(false)
      return
    }
    try {
      const { data } = await sendCampaign({
        variables: { id: saved.id, scheduledFor: scheduledForIso },
      })
      const response = data?.sendCoachCampaign
      if (!response?.success) {
        showError(response?.message)
        setBusy(false)
        return
      }
      if (!user?.isAssumed) {
        amplitudeEvent('coach-campaign-sent', {
          campaign_id: saved.id,
          scheduled: Boolean(scheduledForIso),
          recipient_count: response.campaign.recipientCount,
          offering_count: campaign.offerings.length,
          media_count: campaign.media.length,
        })
      }
      showSuccess(
        scheduledForIso
          ? 'Campaign scheduled'
          : `Sent to ${pluralize(response.campaign.recipientCount, 'student')}`,
      )
      history.push(`${CAMPAIGNS_PATH}/${saved.id}`)
    } catch (e) {
      showError()
      setBusy(false)
    }
  }

  const lastStep = step === STEPS.length - 1

  return (
    <>
      <Helmet>
        <script
          src="https://widget.cloudinary.com/v2.0/global/all.js"
          type="text/javascript"
        />
      </Helmet>
      <PageHeader
        title={initialCampaign.id ? 'Edit Campaign' : 'New Campaign'}
        crumbs={[{ label: 'Campaigns', to: CAMPAIGNS_PATH }]}
      />
      {busy ? <LinearProgress color="primary" /> : null}
      <Box className={classes.content}>
        <Stepper activeStep={step} className={classes.stepper} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 ? (
          <AudienceStep
            campaign={campaign}
            options={options.audience}
            audience={audience}
            audienceLoading={audienceLoading}
            mode={audienceMode}
            pickedStudents={pickedStudents}
            nameError={nameError}
            skipSearch={!hasPaidTier}
            onChange={change}
            onModeChange={changeAudienceMode}
            onPickStudents={pickStudents}
          />
        ) : null}
        {step === 1 ? (
          <ContentStep
            campaign={campaign}
            offeringOptions={options.offerings}
            bodyError={bodyError}
            onChange={change}
          />
        ) : null}
        {step === 2 ? (
          <ReviewStep
            campaign={campaign}
            audienceCount={audienceCount}
            delivery={delivery}
            scheduledFor={scheduledFor}
            minScheduledFor={minScheduledFor}
            maxScheduledFor={maxScheduledFor}
            scheduleError={scheduleError}
            onDeliveryChange={setDelivery}
            onScheduledForChange={value => {
              setScheduledFor(value)
              setScheduleError(null)
            }}
          />
        ) : null}

        <Box className={classes.footerActions}>
          {step > 0 ? (
            <Button disabled={busy} onClick={() => setStep(step - 1)}>
              Back
            </Button>
          ) : null}
          <Box className={classes.footerRight}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.pillButton}
              disabled={busy}
              onClick={saveDraft}
            >
              Save Draft
            </Button>
            {lastStep ? (
              <Button
                variant="contained"
                color="primary"
                className={classes.pillButton}
                disabled={busy || !audienceReady}
                onClick={send}
              >
                {delivery === 'later'
                  ? 'Schedule Campaign'
                  : `Send to ${pluralize(audienceCount, 'Student')}`}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className={classes.pillButton}
                disabled={busy || (step === 0 && !audienceReady)}
                onClick={goNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

CampaignWizardForm.propTypes = {
  initialCampaign: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    audienceFilters: PropTypes.object.isRequired,
    media: PropTypes.array.isRequired,
    offerings: PropTypes.array.isRequired,
  }).isRequired,
  hasPaidTier: PropTypes.bool,
}

const CampaignWizard = () => {
  const { id } = useParams()
  const { user } = useAuth()
  const hasPaidTier = Boolean(user?.coach?.hasPaidTierSubscription)
  const { data, loading, error } = useCoachCampaign(id, {
    skip: !hasPaidTier,
  })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const campaign = data?.coachCampaign
  if (id && hasPaidTier && !campaign) return <Error statusCode={404} />
  if (id && campaign && !campaign.editable) {
    return <Redirect to={`${CAMPAIGNS_PATH}/${id}`} />
  }

  return (
    <CampaignWizardForm
      key={id || 'new'}
      initialCampaign={campaign ? toCampaignState(campaign) : EMPTY_CAMPAIGN}
      hasPaidTier={hasPaidTier}
    />
  )
}

export default CampaignWizard
